import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, Switch } from "@mui/material";
import React from "react";

import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { WORKFLOW_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useToast from "@/contexts/toast-context";
import useWorkflowData from "@/hooks/data/use-workflow-data";
import useUpdateWorkflow from "@/hooks/requests/wokflow/use-update-workflow";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";
import workflowService from "@/services/workflow.service";
import { WorkflowTypes } from "@/types/workflow.types";
import { buildWorkflowUpdatePayload } from "@/utils/workflow-update-payload";

interface WorkflowTableRowProps {
  workflow: IWorkflow;
  index: number;
  messages: WorkflowMessages;
  workflows: IWorkflow[];
  resolveWorkflowTypeLabel?: (workflowTypeId: number) => string;
  onOpenPopover: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    select: IWorkflow
  ) => void;
}

export function WorkflowTableRow({
  workflow,
  index,
  onOpenPopover,
  messages,
  workflows,
  resolveWorkflowTypeLabel,
}: WorkflowTableRowProps) {
  const { update } = useUpdateWorkflow();
  const { mutate } = useMutate();
  const { showToast } = useToast();
  const hasNoPermissionToEdit = useHasNoPermission(WORKFLOW_PERMISSIONS.EDIT);

  const workflowData = useWorkflowData({
    workflows,
    messages,
    resolveWorkflowTypeLabel,
  });
  const { showToastError, getWorkflowTypeLabel, canToggleActive } = workflowData;

  const { name, description, active, workflowTypeId } = workflow;
  const { deactivateStartFlowError } = messages;

  const handleToggleActive = async () => {
    if (!canToggleActive(workflow)) return;

    const { workflowTypeId, active } = workflow;
    const isStartFlowAndActive = workflowTypeId === WorkflowTypes.StartFlow && active;

    if (isStartFlowAndActive) return showToast({ text: deactivateStartFlowError, type: "error" });

    const _id = workflow.id;
    const successMessage = messages.activateWorkflowSuccess;
    const errorMessage = messages.activateWorkflowError;
    const onSuccess = () => {
      void mutate(SWR_KEYS.getWorkflows);
      void mutate(SWR_KEYS.getWorkflowById);
      void mutate(SWR_KEYS.getAllWorkflowTypes);
    };

    try {
      const freshWorkflow = await workflowService.getById(_id);
      const formData = buildWorkflowUpdatePayload(freshWorkflow, {
        active: !freshWorkflow.active,
      });
      await update({ _id, formData, successMessage, errorMessage, onSuccess });
    } catch (error) {
      showToastError(messages.activateWorkflowError);
    }
  };

  return (
    <TableRow index={index}>
      <TableCell>{name}</TableCell>
      <TableCell>{getWorkflowTypeLabel(workflowTypeId)}</TableCell>
      <TableCell>{description}</TableCell>
      <TableCell>
        <Switch
          checked={active}
          disabled={hasNoPermissionToEdit}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleActive();
          }}
        />
      </TableCell>
      <TableCell>
        <IconButton onClick={(e) => onOpenPopover(e, workflow)}>
          <MoreHorizIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
